import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { checkTelegram, checkWhatsApp, type MessengerCheckResponse } from '@/api/messengers';

interface SearchSummaryProps {
  results: Record<string, any[]>;
  operatorInfo?: {
    phone: string;
    operator: string;
    region: string;
    country: string;
  };
  totalResults: number;
}

export function SearchSummary({ results, operatorInfo, totalResults }: SearchSummaryProps) {
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [checkingMessengers, setCheckingMessengers] = useState<Record<string, { telegram?: boolean; whatsapp?: boolean }>>({});
  const [messengerResults, setMessengerResults] = useState<Record<string, { telegram?: MessengerCheckResponse; whatsapp?: MessengerCheckResponse }>>({});

  if (totalResults === 0 && !operatorInfo) {
    return null;
  }

  // Функция для копирования текста
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(null), 2000); // Убираем подсветку через 2 секунды
    } catch (err) {
      console.error('Ошибка при копировании:', err);
    }
  };

  // Функция для проверки в Telegram
  const handleTelegramCheck = async (phone: string) => {
    const phoneKey = phone.toString();
    
    setCheckingMessengers(prev => ({
      ...prev,
      [phoneKey]: { ...prev[phoneKey], telegram: true }
    }));

    try {
      const result = await checkTelegram(phone);
      setMessengerResults(prev => ({
        ...prev,
        [phoneKey]: { ...prev[phoneKey], telegram: result }
      }));
    } catch (error) {
      console.error('Ошибка при проверке Telegram:', error);
    } finally {
      setCheckingMessengers(prev => ({
        ...prev,
        [phoneKey]: { ...prev[phoneKey], telegram: false }
      }));
    }
  };

  // Функция для проверки в WhatsApp
  const handleWhatsAppCheck = async (phone: string) => {
    const phoneKey = phone.toString();
    
    setCheckingMessengers(prev => ({
      ...prev,
      [phoneKey]: { ...prev[phoneKey], whatsapp: true }
    }));

    try {
      const result = await checkWhatsApp(phone);
      setMessengerResults(prev => ({
        ...prev,
        [phoneKey]: { ...prev[phoneKey], whatsapp: result }
      }));
    } catch (error) {
      console.error('Ошибка при проверке WhatsApp:', error);
    } finally {
      setCheckingMessengers(prev => ({
        ...prev,
        [phoneKey]: { ...prev[phoneKey], whatsapp: false }
      }));
    }
  };

  // Извлекаем все уникальные данные из результатов
  const getAllData = () => {
    const personsMap = new Map();
    const mobilePhones = new Set();
    const homePhones = new Set();
    const passports = new Set();
    const inns = new Set();
    const snils = new Set();
    
    // Проходим по всем результатам
    for (const [source, items] of Object.entries(results)) {
      for (const item of items) {
        // Собираем ФИО
        const lastName = item['Фамилия'] || '';
        const firstName = item['Имя'] || '';
        const patronymic = item['Отчество'] || '';
        const fullName = item['Полное имя'] || '';
        const birthDate = item['Дата рождения'] || '';
        
        let personName = '';
        if (fullName) {
          personName = fullName;
        } else if (lastName || firstName) {
          personName = [lastName, firstName, patronymic].filter(Boolean).join(' ');
        }
        
        // Создаем уникальный ключ для личности
        if (personName) {
          const personKey = `${personName}_${birthDate}`;
          if (!personsMap.has(personKey)) {
            personsMap.set(personKey, {
              fullName: personName,
              birthDate: birthDate
            });
          }
        }
        
        // Функция валидации российских номеров
        const isValidRussianPhone = (phone: any): boolean => {
          if (!phone) return false;
          
          // Преобразуем в строку и очищаем от всех нецифровых символов
          const cleanPhone = String(phone).replace(/\D/g, '');
          
          // Проверяем длину (должно быть 10 или 11 цифр)
          if (cleanPhone.length < 10 || cleanPhone.length > 11) {
            return false;
          }
          
          // Если 11 цифр, должно начинаться с 7
          if (cleanPhone.length === 11 && !cleanPhone.startsWith('7')) {
            return false;
          }
          
          // Если 10 цифр, должно начинаться с российских кодов
          if (cleanPhone.length === 10) {
            // Мобильные коды: 9xx
            // Городские коды: 3xx, 4xx, 8xx и другие
            const firstDigit = cleanPhone[0];
            if (firstDigit && !['3', '4', '8', '9'].includes(firstDigit)) {
              return false;
            }
          }
          
          // Исключаем номера типа 79999999999 (все одинаковые цифры после кода)
          if (cleanPhone.length === 11 && cleanPhone.startsWith('7')) {
            const numberPart = cleanPhone.slice(1); // убираем код страны
            // Проверяем, не состоит ли номер из одинаковых цифр
            const uniqueDigits = new Set(numberPart).size;
            if (uniqueDigits <= 2) { // если уникальных цифр 2 или меньше, скорее всего фейк
              return false;
            }
          }
          
          return true;
        };

        // Собираем телефоны (все возможные поля с переводами из FIELD_LABELS)
        const phoneFields = [
          'Телефон',
          'Домашний телефон', 
          'Обновленный телефон',
          'Рабочий телефон',
          'Мобильный телефон',
          'Дополнительный телефон',
          'Дополнительный телефон 1',
          'Дополнительный телефон 2', 
          'Дополнительный телефон 3',
          'Дополнительный телефон 4',
          'Дополнительный телефон 5',
          'Дополнительный телефон 6',
          'Дополнительный телефон 7',
          'Контактные телефоны',
          'Телефон близкого лица'
        ];
        phoneFields.forEach(field => {
          if (item[field] && isValidRussianPhone(item[field])) {
            const cleanPhone = String(item[field]).replace(/\D/g, '');
            
            // Определяем тип номера
            if (cleanPhone.length === 11 && cleanPhone.startsWith('79')) {
              // Мобильный номер
              mobilePhones.add(item[field]);
            } else if (cleanPhone.length === 11 && cleanPhone.startsWith('74')) {
              // Домашний номер
              homePhones.add(item[field]);
            } else if (cleanPhone.length === 10) {
              // 10-значные номера - определяем по первой цифре
              if (cleanPhone.startsWith('9')) {
                mobilePhones.add(item[field]);
              } else {
                homePhones.add(item[field]);
              }
            } else {
              // Остальные 11-значные номера (не 79 и не 74)
              homePhones.add(item[field]);
            }
          }
        });
        
        // Собираем паспорта
        const passport = item['Паспорт'] || item['Серия и номер паспорта'] || item['Данные паспорта'];
        if (passport) {
          passports.add(passport);
        }
        
        // Собираем ИНН
        if (item['ИНН']) {
          inns.add(item['ИНН']);
        }
        
        // Собираем СНИЛС
        if (item['СНИЛС']) {
          snils.add(item['СНИЛС']);
        }
      }
    }
    
    return {
      persons: Array.from(personsMap.values()),
      mobilePhones: Array.from(mobilePhones),
      homePhones: Array.from(homePhones),
      passports: Array.from(passports),
      inns: Array.from(inns),
      snils: Array.from(snils)
    };
  };

  // Извлекаем места работы
  const getWorkplaces = () => {
    const workplaces = new Set<string>();
    
    for (const [source, items] of Object.entries(results)) {
      for (const item of items) {
        // Собираем места работы из различных полей
        const workFields = [
          'Место работы',
          'Рабочее место', 
          'Работа',
          'Рабочий адрес',
          'Места работы 1999',
          'Места работы 2000',
          'Места работы 2001',
          'Места работы 2002',
          'Места работы 2003',
          'Места работы 2004',
          'Места работы 2005-2006',
          'Места работы 2008',
          'Места работы 2009',
          'Места работы 2010',
          'Места работы 2011',
          'Места работы 2012',
          'Места работы 2013'
        ];
        
        workFields.forEach(field => {
          if (item[field]) {
            workplaces.add(item[field]);
          }
        });
      }
    }
    
    return Array.from(workplaces);
  };

  // Извлекаем организации
  const getOrganizations = () => {
    const organizations = new Set<string>();
    
    for (const [source, items] of Object.entries(results)) {
      for (const item of items) {
        // Собираем организации из различных полей
        const orgFields = [
          'Организация',
          'Название организации',
          'Компания',
          'Предприятие',
          'Учреждение',
          'Фирма',
          'ООО',
          'ЗАО',
          'ОАО',
          'ИП'
        ];
        
        orgFields.forEach(field => {
          if (item[field]) {
            organizations.add(item[field]);
          }
        });
      }
    }
    
    return Array.from(organizations);
  };

  const { persons, mobilePhones, homePhones, passports, inns, snils } = getAllData();
  const workplaces = getWorkplaces();
  const organizations = getOrganizations();
  const sourcesCount = Object.keys(results).length;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg">Краткая сводка</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {/* Личности */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-muted-foreground">ЛИЧНОСТИ ({persons.length})</h4>
            {persons.length > 0 ? (
              <div className="space-y-1">
                {persons.map((person, index) => {
                  const personText = person.fullName && person.birthDate 
                    ? `${person.fullName} ${person.birthDate}`
                    : person.fullName || person.birthDate;
                  return (
                    <p 
                      key={index} 
                      className={`text-sm cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-colors ${
                        copiedText === personText ? 'bg-green-100 text-green-800' : ''
                      }`}
                      onClick={() => copyToClipboard(personText)}
                      title="Нажмите для копирования"
                    >
                      {personText}
                    </p>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Не найдено</p>
            )}
          </div>

          {/* Мобильные телефоны */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-muted-foreground">МОБИЛЬНЫЕ ({mobilePhones.length})</h4>
            {operatorInfo && (
              <div>
                <span className="text-sm font-medium">Оператор:</span>
                <p className="text-sm">{operatorInfo.operator}</p>
                <p className="text-xs text-muted-foreground">{operatorInfo.region}</p>
              </div>
            )}
            {mobilePhones.length > 0 ? (
              <div className="space-y-2">
                {mobilePhones.map((phone, index) => {
                  const phoneText = String(phone);
                  const phoneKey = phoneText;
                  const telegramResult = messengerResults[phoneKey]?.telegram;
                  const whatsappResult = messengerResults[phoneKey]?.whatsapp;
                  const isCheckingTelegram = checkingMessengers[phoneKey]?.telegram;
                  const isCheckingWhatsApp = checkingMessengers[phoneKey]?.whatsapp;
                  
                  return (
                    <div key={index} className="space-y-1">
                      <p 
                        className={`text-sm cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-colors ${
                          copiedText === phoneText ? 'bg-green-100 text-green-800' : ''
                        }`}
                        onClick={() => copyToClipboard(phoneText)}
                        title="Нажмите для копирования"
                      >
                        {phoneText}
                      </p>
                      
                      {/* Кнопки проверки мессенджеров */}
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 w-6 p-0"
                          onClick={() => handleTelegramCheck(phoneText)}
                          disabled={isCheckingTelegram}
                          title="Проверить в Telegram"
                        >
                          {isCheckingTelegram ? (
                            <span className="text-xs">...</span>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                            </svg>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 w-6 p-0"
                          onClick={() => handleWhatsAppCheck(phoneText)}
                          disabled={isCheckingWhatsApp}
                          title="Проверить в WhatsApp"
                        >
                          {isCheckingWhatsApp ? (
                            <span className="text-xs">...</span>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                            </svg>
                          )}
                        </Button>
                      </div>
                      
                      {/* Результаты проверки */}
                      {telegramResult && (
                        <div className={`text-xs px-2 py-1 rounded ${
                          telegramResult.found ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {telegramResult.display_message || telegramResult.message}
                        </div>
                      )}
                      {whatsappResult && (
                        <div className={`text-xs px-2 py-1 rounded ${
                          whatsappResult.found ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {whatsappResult.message}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : !operatorInfo ? (
              <p className="text-sm text-muted-foreground">Не найдено</p>
            ) : null}
          </div>

          {/* Домашние телефоны */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-muted-foreground">ДОМАШНИЕ ({homePhones.length})</h4>
            {homePhones.length > 0 ? (
              <div className="space-y-1">
                {homePhones.map((phone, index) => {
                  const phoneText = String(phone);
                  return (
                    <p 
                      key={index} 
                      className={`text-sm cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-colors ${
                        copiedText === phoneText ? 'bg-green-100 text-green-800' : ''
                      }`}
                      onClick={() => copyToClipboard(phoneText)}
                      title="Нажмите для копирования"
                    >
                      {phoneText}
                    </p>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Не найдено</p>
            )}
          </div>

          {/* Паспорт */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-muted-foreground">ПАСПОРТ ({passports.length})</h4>
            {passports.length > 0 ? (
              <div className="space-y-1">
                {passports.map((passport, index) => {
                  const passportText = String(passport);
                  return (
                    <p 
                      key={index} 
                      className={`text-sm cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-colors ${
                        copiedText === passportText ? 'bg-green-100 text-green-800' : ''
                      }`}
                      onClick={() => copyToClipboard(passportText)}
                      title="Нажмите для копирования"
                    >
                      {passportText}
                    </p>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Не найдено</p>
            )}
          </div>

          {/* ИНН */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-muted-foreground">ИНН ({inns.length})</h4>
            {inns.length > 0 ? (
              <div className="space-y-1">
                {inns.map((inn, index) => {
                  const innText = String(inn);
                  return (
                    <p 
                      key={index} 
                      className={`text-sm cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-colors ${
                        copiedText === innText ? 'bg-green-100 text-green-800' : ''
                      }`}
                      onClick={() => copyToClipboard(innText)}
                      title="Нажмите для копирования"
                    >
                      {innText}
                    </p>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Не найдено</p>
            )}
          </div>
        </div>

        {/* СНИЛС - отдельная строка */}
        <div className="mt-4 pt-4 border-t">
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-muted-foreground">СНИЛС ({snils.length})</h4>
            {snils.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {snils.map((snilsItem, index) => {
                  const snilsText = String(snilsItem);
                  return (
                    <span 
                      key={index} 
                      className={`text-sm px-2 py-1 rounded cursor-pointer transition-colors ${
                        copiedText === snilsText 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-muted hover:bg-muted/70'
                      }`}
                      onClick={() => copyToClipboard(snilsText)}
                      title="Нажмите для копирования"
                    >
                      {snilsText}
                    </span>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Не найдено</p>
            )}
          </div>
        </div>

        {/* Место работы */}
        {workplaces.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-muted-foreground">МЕСТО РАБОТЫ ({workplaces.length})</h4>
              <div className="space-y-1">
                {workplaces.map((workplace, index) => {
                  const workplaceText = String(workplace);
                  return (
                    <p 
                      key={index} 
                      className={`text-sm cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-colors ${
                        copiedText === workplaceText ? 'bg-green-100 text-green-800' : ''
                      }`}
                      onClick={() => copyToClipboard(workplaceText)}
                      title="Нажмите для копирования"
                    >
                      {workplaceText}
                    </p>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Организации */}
        {organizations.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-muted-foreground">ОРГАНИЗАЦИЯ ({organizations.length})</h4>
              <div className="space-y-1">
                {organizations.map((organization, index) => {
                  const organizationText = String(organization);
                  return (
                    <p 
                      key={index} 
                      className={`text-sm cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-colors ${
                        copiedText === organizationText ? 'bg-green-100 text-green-800' : ''
                      }`}
                      onClick={() => copyToClipboard(organizationText)}
                      title="Нажмите для копирования"
                    >
                      {organizationText}
                    </p>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Статистика */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">
              Найдено записей: {totalResults}
            </Badge>
            <Badge variant="secondary">
              Источников: {sourcesCount}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
